// =====================================================
// OUTPUT PACKAGE: the parts of the redline that are not its story text.
//
// The redline is the revised draft's package with a new body: every other part is copied from the
// revised draft, then the parts the output itself needs are added (the watermark's header and
// footer, revision tracking in the settings, the authors list, custom properties).
// =====================================================

import { xmlEscape } from './xml-helpers';
import { addRelationship, declarePart, REL, CONTENT_TYPE } from './package-parts';
import { hasIncomingRevisions } from './docx-tracking';
import { runsWithWordmark } from './wordmark';
import type { DiffParagraph, ParsedDocument } from '../types';

// ── VML diagonal watermark (appears on every page via header) ──
export const WATERMARK_HEADER_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:hdr xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
       xmlns:v="urn:schemas-microsoft-com:vml"
       xmlns:o="urn:schemas-microsoft-com:office:office"
       xmlns:w10="urn:schemas-microsoft-com:office:word"
       xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <w:p>
    <w:pPr><w:pStyle w:val="Header"/></w:pPr>
    <w:r>
      <w:rPr><w:noProof/></w:rPr>
      <w:pict>
        <v:shapetype id="_x0000_t136" coordsize="21600,21600"
          o:spt="136" adj="10800"
          path="m@7,l@8,m@5,21600l@6,21600e">
          <v:formulas>
            <v:f eqn="sum #0 0 10800"/>
            <v:f eqn="prod #0 2 1"/>
            <v:f eqn="sum 21600 0 @1"/>
            <v:f eqn="sum 0 0 @2"/>
            <v:f eqn="sum 21600 0 @3"/>
            <v:f eqn="if @0 @3 0"/>
            <v:f eqn="if @0 21600 @1"/>
            <v:f eqn="if @0 0 @2"/>
            <v:f eqn="if @0 @4 21600"/>
            <v:f eqn="mid @5 @6"/>
            <v:f eqn="mid @8 @5"/>
            <v:f eqn="mid @7 @8"/>
            <v:f eqn="mid @6 @7"/>
            <v:f eqn="sum @6 0 @5"/>
          </v:formulas>
          <v:path textpathok="t" o:connecttype="custom"
            o:connectlocs="@9,0;@10,10800;@11,21600;@12,10800"
            o:connectangles="270,180,90,0"/>
          <v:textpath on="t" fitshape="t"/>
          <v:handles>
            <v:h position="#0,bottomRight" xrange="6629,14971"/>
          </v:handles>
          <o:lock v:ext="edit" text="t" shapetype="t"/>
        </v:shapetype>
        <v:shape id="PowerPlusWaterMarkObject"
          o:spid="_x0000_s2049"
          o:allowincell="f"
          type="#_x0000_t136"
          style="position:absolute;margin-left:0;margin-top:0;width:500pt;height:80pt;rotation:315;z-index:-251656192;mso-position-horizontal:center;mso-position-horizontal-relative:margin;mso-position-vertical:center;mso-position-vertical-relative:margin"
          fillcolor="#C0C0C0"
          stroked="f">
          <v:fill opacity=".25"/>
          <v:textpath style="font-family:&quot;Calibri&quot;;font-size:1pt"
            string="Generated with Fredline"/>
          <w10:wrap anchorx="margin" anchory="margin"/>
        </v:shape>
      </w:pict>
    </w:r>
  </w:p>
</w:hdr>`;

// ── Footer with Fredline branding ──
export const WATERMARK_FOOTER_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:ftr xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
       xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <w:p>
    <w:pPr><w:pStyle w:val="Footer"/><w:jc w:val="center"/></w:pPr>
    ${runsWithWordmark('Generated with Fredline \u2014 fredline.app', (t) => `<w:r><w:rPr><w:color w:val="A0A0A0"/><w:sz w:val="16"/><w:szCs w:val="16"/></w:rPr><w:t xml:space="preserve">${t}</w:t></w:r>`, { size: 8 })}
  </w:p>
</w:ftr>`;

/** The output starts as the revised draft's package, every part but the body copied (or, from a
 *  draft that is not a .docx, a minimal package), with the watermark's header and footer. */
export async function copyRevisedPackage(newZip: any, revDocData: ParsedDocument, hasDocxBase: boolean, addWatermark: boolean, useTrackChanges: boolean, headerRId: string, footerRId: string): Promise<void> {
  if (hasDocxBase) {
    const baseZip = revDocData.zip;
    for (const [path, file] of Object.entries(baseZip.files) as [string, any][]) {
      if (file.dir) continue;
      // [trash] holds unreferenced parts Word leaves behind; copying them forward
      // carries a part with no content-type declaration → repair prompt.
      if (path.startsWith('[trash]')) continue;
      if (path === 'word/document.xml') continue;
      // If adding watermark, we'll rewrite the rels and content types ourselves
      if (addWatermark && path === 'word/_rels/document.xml.rels') continue;
      if (addWatermark && path === '[Content_Types].xml') continue;
      const content = await file.async('uint8array');
      newZip.file(path, content);
    }
    for (const [path, file] of Object.entries(baseZip.files) as [string, any][]) {
      if (file.dir) newZip.folder(path);
    }

    if (addWatermark) {
      // Re-add [Content_Types].xml with header + footer overrides
      const ctFile = baseZip.file('[Content_Types].xml');
      let ctXml = ctFile ? await ctFile.async('string') : '';
      if (ctXml && !ctXml.includes('header1.xml')) {
        ctXml = ctXml.replace('</Types>',
          '<Override PartName="/word/header1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml"/></Types>');
      }
      if (ctXml && !ctXml.includes('footer1.xml')) {
        ctXml = ctXml.replace('</Types>',
          '<Override PartName="/word/footer1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.footer+xml"/></Types>');
      }
      newZip.file('[Content_Types].xml', ctXml);

      // Re-add document.xml.rels with header + footer relationships
      const relsFile = baseZip.file('word/_rels/document.xml.rels');
      let relsXml = relsFile ? await relsFile.async('string') : '';
      if (relsXml && !relsXml.includes(headerRId)) {
        relsXml = relsXml.replace('</Relationships>',
          `<Relationship Id="${headerRId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/header" Target="header1.xml"/></Relationships>`);
      }
      if (relsXml && !relsXml.includes(footerRId)) {
        relsXml = relsXml.replace('</Relationships>',
          `<Relationship Id="${footerRId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/footer" Target="footer1.xml"/></Relationships>`);
      }
      newZip.file('word/_rels/document.xml.rels', relsXml);

      // Add the watermark header + footer
      newZip.file('word/header1.xml', WATERMARK_HEADER_XML);
      newZip.file('word/footer1.xml', WATERMARK_FOOTER_XML);
    }
  } else {
    const wmContentTypes = addWatermark
      ? '<Override PartName="/word/header1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml"/>' +
        '<Override PartName="/word/footer1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.footer+xml"/>'
      : '';
    newZip.file('[Content_Types].xml',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
      '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
      '<Default Extension="xml" ContentType="application/xml"/>' +
      '<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>' +
      (useTrackChanges ? '<Override PartName="/word/settings.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.settings+xml"/>' : '') +
      wmContentTypes +
      '</Types>');
    newZip.file('_rels/.rels',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
      '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>' +
      '</Relationships>');
    const wmRels = addWatermark
      ? `<Relationship Id="${headerRId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/header" Target="header1.xml"/>` +
        `<Relationship Id="${footerRId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/footer" Target="footer1.xml"/>`
      : '';
    newZip.file('word/_rels/document.xml.rels',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
      (useTrackChanges ? '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/settings" Target="settings.xml"/>' : '') +
      wmRels +
      '</Relationships>');

    if (addWatermark) {
      newZip.file('word/header1.xml', WATERMARK_HEADER_XML);
      newZip.file('word/footer1.xml', WATERMARK_FOOTER_XML);
    }
  }
}

/** Track Changes output: revision tracking on in the settings, and every revision author in
 *  people.xml. */
export async function addTrackChangesParts(newZip: any, revDocData: ParsedDocument, hasDocxBase: boolean, useTrackChanges: boolean, revAuthor: string, paragraphs: DiffParagraph[]): Promise<void> {
  // Inject Track Changes settings + people.xml
  if (useTrackChanges) {
    const tcAuthor = revAuthor;

    if (hasDocxBase) {
      const settingsFile = revDocData.zip.file('word/settings.xml');
      if (settingsFile) {
        let settingsXml = await settingsFile.async('string');
        if (!settingsXml.includes('<w:trackRevisions')) {
          // CT_Settings is an ordered sequence — trackRevisions is NOT last, so
          // appending it before </w:settings> puts it after later-ordered elements
          // and fails schema validation. Insert it before the first element that
          // sorts after it; fall back to the end only if none are present.
          // Every CT_Settings element that sorts AFTER trackRevisions. Insert before
          // whichever of these appears earliest in the document.
          const after = ['doNotTrackMoves', 'doNotTrackFormatting', 'documentProtection',
            'autoFormatOverride', 'styleLockTheme', 'styleLockQFSet', 'defaultTabStop',
            'autoHyphenation', 'consecutiveHyphenLimit', 'hyphenationZone', 'doNotHyphenateCaps',
            'showEnvelope', 'summaryLength', 'clickAndTypeStyle', 'defaultTableStyle',
            'evenAndOddHeaders', 'bookFoldRevPrinting', 'bookFoldPrinting', 'bookFoldPrintingSheets',
            'drawingGridHorizontalSpacing', 'drawingGridVerticalSpacing', 'displayHorizontalDrawingGridEvery',
            'displayVerticalDrawingGridEvery', 'doNotUseMarginsForDrawingGridOrigin',
            'drawingGridHorizontalOrigin', 'drawingGridVerticalOrigin', 'doNotShadeFormData',
            'noPunctuationKerning', 'characterSpacingControl', 'printTwoOnOne', 'strictFirstAndLastChars',
            'noLineBreaksAfter', 'noLineBreaksBefore', 'savePreviewPicture', 'doNotValidateAgainstSchema',
            'saveInvalidXml', 'ignoreMixedContent', 'alwaysShowPlaceholderText', 'doNotDemarcateInvalidXml',
            'saveXmlDataOnly', 'useXSLTWhenSaving', 'saveThroughXslt', 'showXMLTags',
            'alwaysMergeEmptyNamespace', 'updateFields', 'hdrShapeDefaults', 'footnotePr', 'endnotePr',
            'compat', 'rsids', 'mathPr', 'attachedSchema', 'themeFontLang', 'clrSchemeMapping',
            'doNotIncludeSubdocsInStats', 'doNotAutoCompressPictures', 'forceUpgrade', 'captions',
            'readModeInkLockDown', 'smartTagType', 'schemaLibrary', 'shapeDefaults', 'doNotEmbedSmartTags',
            'decimalSymbol', 'listSeparator']
            .map(t => ({ a: '<w:' + t, i: settingsXml.indexOf('<w:' + t) })).filter(x => x.i >= 0)
            .sort((x, y) => x.i - y.i)[0];
          settingsXml = after
            ? settingsXml.slice(0, after.i) + '<w:trackRevisions/>' + settingsXml.slice(after.i)
            : settingsXml.replace('</w:settings>', '<w:trackRevisions/></w:settings>');
        }
        newZip.file('word/settings.xml', settingsXml);
      } else {
        newZip.file('word/settings.xml',
          '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
          '<w:settings xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">' +
          '<w:trackRevisions/>' +
          '</w:settings>');
      }
    } else {
      newZip.file('word/settings.xml',
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<w:settings xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">' +
        '<w:trackRevisions/>' +
        '</w:settings>');
    }

    // Generate people.xml — lists revision authors (required by Word 2013+). Collect
    // the run author plus any incoming authors whose tracked changes survive into the
    // output (preserved verbatim in Combine mode, or carried on equal paragraphs), so
    // every w:author in the body is declared. Author values pulled from raw XML are
    // already entity-escaped; the run author is escaped here, and the set dedupes on the
    // escaped form so nothing is double-escaped.
    const authorSetEsc = new Set<string>([xmlEscape(tcAuthor)]);
    const authorRe = /w:author="([^"]*)"/g;
    for (const p of paragraphs) {
      for (const rich of [p.revRich, p.origRich]) {
        if (!rich?.rawXml || !hasIncomingRevisions(rich.rawXml)) continue;
        let am: RegExpExecArray | null;
        while ((am = authorRe.exec(rich.rawXml)) !== null) authorSetEsc.add(am[1]);
      }
    }
    const personsXml = Array.from(authorSetEsc).map(a =>
      `<w15:person w15:author="${a}"><w15:presenceInfo w15:providerId="None" w15:userId="${a}"/></w15:person>`
    ).join('');
    newZip.file('word/people.xml',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<w15:people xmlns:w15="http://schemas.microsoft.com/office/word/2012/wordml"' +
      ' xmlns:mc="http://schemas.openxmlformats.org/markup-compatibility/2006"' +
      ' mc:Ignorable="w15">' +
      personsXml + '</w15:people>');

    await declarePart(newZip, 'word/people.xml', CONTENT_TYPE.people);
    const docRels: string | undefined = await newZip.file('word/_rels/document.xml.rels')?.async('string');
    if (docRels && !docRels.includes('people.xml')) await addRelationship(newZip, 'word/_rels/document.xml.rels', { id: 'rIdPeople', type: REL.people, target: 'people.xml' });
  }
}

export async function addCustomProperties(newZip: any, addWatermark: boolean, licensee: string | undefined): Promise<void> {
  // Add custom document properties (watermark branding and/or licensee fingerprint)
  const needsCustomProps = addWatermark || !!licensee;
  if (needsCustomProps) {
    let pid = 2;
    let props = '';
    if (addWatermark) {
      props +=
        `<property fmtid="{D5CDD505-2E9C-101B-9397-08002B2CF9AE}" pid="${pid++}" name="Generator">` +
        '<vt:lpwstr>Fredline Free — fredline.app</vt:lpwstr></property>' +
        `<property fmtid="{D5CDD505-2E9C-101B-9397-08002B2CF9AE}" pid="${pid++}" name="License">` +
        '<vt:lpwstr>Free version — upgrade at fredline.app/pricing</vt:lpwstr></property>';
    }
    if (licensee) {
      props +=
        `<property fmtid="{D5CDD505-2E9C-101B-9397-08002B2CF9AE}" pid="${pid++}" name="FredlineLicensee">` +
        `<vt:lpwstr>${xmlEscape(licensee)}</vt:lpwstr></property>` +
        `<property fmtid="{D5CDD505-2E9C-101B-9397-08002B2CF9AE}" pid="${pid++}" name="FredlineBuildId">` +
        `<vt:lpwstr>${xmlEscape(licensee)}-${new Date().toISOString().slice(0, 10)}</vt:lpwstr></property>`;
    }
    newZip.file('docProps/custom.xml',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/custom-properties" ' +
      'xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">' +
      props +
      '</Properties>');

    await declarePart(newZip, 'docProps/custom.xml', CONTENT_TYPE.customProperties);
    // (a free id: a fixed rId3 collided with the app.xml relationship most documents have)
    const rootRels: string | undefined = await newZip.file('_rels/.rels')?.async('string');
    if (rootRels && !rootRels.includes('custom-properties')) await addRelationship(newZip, '_rels/.rels', { type: REL.customProperties, target: 'docProps/custom.xml' });
  }
}
